import {Trade} from '@uniswap/router-sdk';
import { Currency, Percent, TradeType } from '@uniswap/sdk-core';
import { SwapRouter } from '@uniswap/universal-router-sdk';
import { toHex } from '@uniswap/v3-sdk';
import { useTokenApi } from 'api/Token';
import { UNIVERSAL_ROUTER_ADDRESS } from 'constants/addresses';
import { SupportedChainId } from 'constants/chains';
import { WRAPPED_NATIVE_CURRENCY } from 'constants/tokens';
import { HistoryDuration } from 'graphql/data/__generated__/types-and-hooks';
import { useCallback, useEffect, useState } from 'react';
import isZero from 'utils/isZero';

import { PermitSignature } from './usePermitAllowance';
import useTransactionDeadline from './useTransactionDeadline';
import {useAccount} from "wagmi";
import {useEthersProvider} from "../connection/wallet-connect";
import {ethers} from "ethers";

type SwapGasCostProps = {
  trade: Trade<Currency, Currency, TradeType> | undefined,
  slippageTolerance: Percent
  permit?: PermitSignature
}

export const useSwapGasCostUsd = ({ trade, slippageTolerance, permit }: SwapGasCostProps) => {
  const [gasCostUsd, setGasCostUsd] = useState(0)
  const deadline = useTransactionDeadline()
  const { address: account, chainId } = useAccount()
  const provider = useEthersProvider()
  const { data: wrappedNativeTokenData } = useTokenApi(
    WRAPPED_NATIVE_CURRENCY[chainId ?? SupportedChainId.CRAT_TESTNET]?.address,
    HistoryDuration.Day,
  )

  const getSwapGasCostUsd = useCallback(async () => {
    try {
      if (!account || !chainId || !provider || !trade) return

      const {calldata: data, value} = SwapRouter.swapERC20CallParameters(trade, {
        slippageTolerance,
        deadlineOrPreviousBlockhash: deadline?.toString(),
        inputTokenPermit: permit,
      })

      const tx = {
        from: account,
        to: UNIVERSAL_ROUTER_ADDRESS[chainId],
        data,
        ...(value && !isZero(value) ? { value: toHex(value) } : {}),
      }

      const gasPrice = await provider?.getGasPrice()
      const gasEstimate = await provider.estimateGas(tx)
      const maxPriorityFeePerGas = ethers.utils.parseUnits('2', 'gwei')
      const gasCost = gasPrice.add(maxPriorityFeePerGas).mul(gasEstimate)
      const gasCostUsd = gasCost.toNumber() * +(wrappedNativeTokenData?.priceUsd ?? 0) / 10 ** 18
      setGasCostUsd(gasCostUsd)
    } catch (err) {
      console.error('getSwapGasCostUsd error:', err)
    }
  }, [account, chainId, deadline, permit, provider, slippageTolerance, trade, wrappedNativeTokenData?.priceUsd])

  useEffect(() => {
    getSwapGasCostUsd()
  }, [getSwapGasCostUsd])

  return gasCostUsd
}
